import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Chip
} from '@mui/material';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import SaveIcon from '@mui/icons-material/Save';
import FolderIcon from '@mui/icons-material/Folder';
import HomeIcon from '@mui/icons-material/Home';
import { useTranslation } from 'react-i18next';

interface ImageItem {
  id: string;
  name: string;
  url: string;
  thumbnailUrl?: string;
  folderId: string;
  createdAt: string;
  createdBy: string;
  size?: number;
  type?: string;
}

interface ImageFolder {
  id: string;
  name: string;
  createdAt: string;
  createdBy: string;
  imageCount?: number;
}

interface ImageMoveModalProps {
  open: boolean;
  onClose: () => void;
  image: ImageItem | null;
  onMove: (imageId: string, newFolderId: string) => Promise<void>;
  availableFolders: ImageFolder[];
  currentFolderId: string;
}

const ImageMoveModal: React.FC<ImageMoveModalProps> = ({
  open,
  onClose,
  image,
  onMove,
  availableFolders,
  currentFolderId
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const [selectedFolderId, setSelectedFolderId] = useState<string>('');
  const [isMoving, setIsMoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal açıldığında mevcut klasörü seç
  React.useEffect(() => {
    if (open) {
      setSelectedFolderId(currentFolderId);
      setError(null);
    }
  }, [open, currentFolderId]);

  // Modal kapanırken temizle
  const handleClose = () => {
    if (isMoving) return;
    setSelectedFolderId('');
    setError(null);
    onClose();
  };

  // Resmi taşı
  const handleMove = async () => {
    if (!image) return;

    // Aynı klasöre taşımaya çalışıyorsa
    if (selectedFolderId === currentFolderId) {
      setError(tt('Resim zaten bu klasörde bulunuyor', 'Image is already in this folder'));
      return;
    }

    setIsMoving(true);
    setError(null);

    try {
      await onMove(image.id, selectedFolderId);
      
      // State güncellemesinin tamamlanması için kısa bir gecikme
      await new Promise(resolve => setTimeout(resolve, 100));
      
      handleClose();
    } catch (error: any) {
      console.error('Resim taşıma hatası:', error);
      // Backend'den gelen hata mesajını göster
      const errorMessage = error?.message || tt('Resim taşınırken bir hata oluştu', 'An error occurred while moving image');
      setError(errorMessage);
    } finally {
      setIsMoving(false);
    }
  };

  // Dosya boyutunu formatla
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Mevcut klasör adını bul
  const getCurrentFolderName = () => {
    if (!currentFolderId) return tt('Ana Dizin', 'Main Directory');
    const folder = availableFolders.find(f => f.id === currentFolderId);
    return folder ? folder.name : tt('Bilinmeyen Klasör', 'Unknown Folder');
  };

  // Hedef klasör adını bul
  const getTargetFolderName = () => {
    if (!selectedFolderId) return tt('Ana Dizin', 'Main Directory');
    const folder = availableFolders.find(f => f.id === selectedFolderId);
    return folder ? folder.name : tt('Bilinmeyen Klasör', 'Unknown Folder');
  };

  if (!image) return null;

  return (
    <Dialog
      open={open}
      onClose={!isMoving ? handleClose : undefined}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <DriveFileMoveIcon sx={{ mr: 1 }} />
        {tt('Resmi Taşı', 'Move Image')}
      </DialogTitle>
      
      <DialogContent>
        {/* Resim Bilgileri */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          <Box
            component="img"
            src={image.url}
            alt={image.name}
            sx={{
              width: 60,
              height: 60,
              objectFit: 'cover',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.300',
              mr: 2
            }}
          />
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {image.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {image.size ? formatFileSize(image.size) : tt('Bilinmiyor', 'Unknown')} • {new Date(image.createdAt).toLocaleDateString(localeCode)}
            </Typography>
          </Box>
        </Box>

        {/* Mevcut Konum */}
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {tt('Mevcut Konum:', 'Current Location:')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {currentFolderId ? <FolderIcon sx={{ mr: 1, color: 'primary.main' }} /> : <HomeIcon sx={{ mr: 1, color: 'primary.main' }} />}
            <Typography variant="subtitle2" fontWeight="bold">
              {getCurrentFolderName()}
            </Typography>
          </Box>
        </Box>

        {/* Hedef Klasör Seçimi */}
        <FormControl fullWidth margin="dense">
          <InputLabel>{tt('Hedef Klasör', 'Target Folder')}</InputLabel>
          <Select
            value={selectedFolderId}
            label={tt('Hedef Klasör', 'Target Folder')}
            onChange={(e) => setSelectedFolderId(e.target.value)}
            disabled={isMoving}
          >
            <MenuItem value="">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <HomeIcon sx={{ mr: 1 }} />
                {tt('Ana Dizin', 'Main Directory')}
              </Box>
            </MenuItem>
            {availableFolders.map((folder) => (
              <MenuItem key={folder.id} value={folder.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FolderIcon sx={{ mr: 1 }} />
                    {folder.name}
                  </Box>
                  <Chip 
                    label={`${folder.imageCount || 0} ${tt('resim', 'images')}`} 
                    size="small" 
                    variant="outlined"
                  />
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Taşıma Önizlemesi */}
        {selectedFolderId !== currentFolderId && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
            <Typography variant="body2" color="success.contrastText">
              <strong>{image.name}</strong> {tt('resmi', 'image')} <strong>{getTargetFolderName()}</strong> {tt('konumuna taşınacak.', 'will be moved to location.')}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={handleClose} 
          color="inherit"
          disabled={isMoving}
        >
          {tt('İptal', 'Cancel')}
        </Button>
        <Button 
          onClick={handleMove} 
          variant="contained"
          color="primary" 
          disabled={isMoving || selectedFolderId === currentFolderId}
          startIcon={isMoving ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {isMoving ? tt('Taşınıyor...', 'Moving...') : tt('Taşı', 'Move')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageMoveModal; 